import { MARD_291, hexToRgb, readableTextColor } from './palette';
import type { PatternProject } from '../types';

export const PRINT_CELL_SIZE_MM = 2.7;
export const PRINT_MAX_WIDTH = 72;
export const PRINT_MAX_HEIGHT = 96;

export type PatternPrintMode = 'color' | 'monochrome';

interface PrintColorStat {
  code: string;
  hex: string;
  count: number;
}

const PRINT_CONTENT_TOP_MM = 9;
const PRINT_CONTENT_BOTTOM_MM = 4;
const PRINT_CONTENT_HEIGHT_MM = 297 - PRINT_CONTENT_TOP_MM - PRINT_CONTENT_BOTTOM_MM;
const PRINT_LEGEND_COLUMNS = 9;
const PRINT_LEGEND_ITEM_HEIGHT_MM = 6;
const PRINT_LEGEND_ROW_GAP_MM = 1;
const PRINT_LEGEND_SECTION_CHROME_MM = 3.2;

function roundMillimeters(value: number) {
  return Math.round(value * 10) / 10;
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function grayscaleHex(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  const channel = Math.round(r * 0.299 + g * 0.587 + b * 0.114).toString(16).padStart(2, '0');
  return `#${channel}${channel}${channel}`;
}

function collectPrintStats(project: PatternProject): PrintColorStat[] {
  const counts = new Map<number, number>();
  for (let index = 0; index < project.colorIndices.length; index += 1) {
    if (project.empty[index]) continue;
    const paletteIndex = project.colorIndices[index];
    counts.set(paletteIndex, (counts.get(paletteIndex) || 0) + 1);
  }
  return [...counts.entries()].map(([paletteIndex, count]) => ({
    code: MARD_291[paletteIndex]?.code || `#${paletteIndex}`,
    hex: MARD_291[paletteIndex]?.hex || '#ffffff',
    count
  })).sort((left, right) => left.code.localeCompare(right.code, 'en', { numeric: true, sensitivity: 'base' }));
}

export function getPatternPrintError(project: PatternProject) {
  const over: string[] = [];
  if (project.width > PRINT_MAX_WIDTH) over.push(`宽度超过 ${PRINT_MAX_WIDTH} 格`);
  if (project.height > PRINT_MAX_HEIGHT) over.push(`高度超过 ${PRINT_MAX_HEIGHT} 格`);
  if (over.length) return `当前图纸为 ${project.width} × ${project.height} 格，${over.join('、')}。打印版最大支持 ${PRINT_MAX_WIDTH} × ${PRINT_MAX_HEIGHT} 格，大图拆解打印将在后续版本提供。`;
  const metrics = getPatternPrintMetrics(project);
  if (metrics.requiredHeightMm > PRINT_CONTENT_HEIGHT_MM) {
    return `当前图纸有 ${metrics.colorCount} 种用色，${PRINT_CELL_SIZE_MM}mm网格与完整色卡合计需要 ${metrics.requiredHeightMm.toFixed(1)}mm，高于A4单页可用的 ${PRINT_CONTENT_HEIGHT_MM}mm。单页模式不会缩小格子或拆成第二页，请减少图纸高度或用色数量。`;
  }
  return '';
}

export function getPatternPrintMetrics(project: PatternProject) {
  const stats = collectPrintStats(project);
  const legendRows = Math.ceil(stats.length / PRINT_LEGEND_COLUMNS);
  const legendHeightMm = legendRows > 0
    ? legendRows * PRINT_LEGEND_ITEM_HEIGHT_MM + (legendRows - 1) * PRINT_LEGEND_ROW_GAP_MM
    : 0;
  const heightMm = roundMillimeters(project.height * PRINT_CELL_SIZE_MM);
  const requiredHeightMm = roundMillimeters(heightMm + (legendRows ? PRINT_LEGEND_SECTION_CHROME_MM + legendHeightMm : 0));
  return {
    widthMm: roundMillimeters(project.width * PRINT_CELL_SIZE_MM),
    heightMm,
    colorCount: stats.length,
    beadCount: stats.reduce((sum, item) => sum + item.count, 0),
    legendColumns: PRINT_LEGEND_COLUMNS,
    legendRows,
    legendHeightMm,
    requiredHeightMm,
    fitsSinglePage: requiredHeightMm <= PRINT_CONTENT_HEIGHT_MM
  };
}

function buildPatternSvg(project: PatternProject, mode: PatternPrintMode) {
  const fills: string[] = [];
  const codes: string[] = [];
  for (let row = 0; row < project.height; row += 1) {
    for (let col = 0; col < project.width; col += 1) {
      const index = row * project.width + col;
      if (project.empty[index]) continue;
      const color = MARD_291[project.colorIndices[index]];
      if (!color) continue;
      if (mode === 'color') fills.push(`<rect x="${col}" y="${row}" width="1" height="1" fill="${color.hex}"/>`);
      const textColor = mode === 'color' ? readableTextColor(color.hex) : '#111111';
      codes.push(`<text x="${col + 0.5}" y="${row + 0.53}" fill="${textColor}">${escapeHtml(color.code)}</text>`);
    }
  }

  const lines: string[] = [];
  for (let col = 0; col <= project.width; col += 1) {
    const major = col === 0 || col === project.width || col % 10 === 0;
    const guide = !major && col % 5 === 0;
    const width = major ? 0.1 : guide ? 0.065 : 0.025;
    const stroke = major ? '#151515' : guide ? '#555555' : '#9b9b9b';
    lines.push(`<line x1="${col}" y1="0" x2="${col}" y2="${project.height}" stroke="${stroke}" stroke-width="${width}"/>`);
  }
  for (let row = 0; row <= project.height; row += 1) {
    const major = row === 0 || row === project.height || row % 10 === 0;
    const guide = !major && row % 5 === 0;
    const width = major ? 0.1 : guide ? 0.065 : 0.025;
    const stroke = major ? '#151515' : guide ? '#555555' : '#9b9b9b';
    lines.push(`<line x1="0" y1="${row}" x2="${project.width}" y2="${row}" stroke="${stroke}" stroke-width="${width}"/>`);
  }

  const widthMm = roundMillimeters(project.width * PRINT_CELL_SIZE_MM);
  const heightMm = roundMillimeters(project.height * PRINT_CELL_SIZE_MM);
  return `<svg class="pattern-grid" width="${widthMm}mm" height="${heightMm}mm" viewBox="0 0 ${project.width} ${project.height}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${project.width}列${project.height}行打印图纸">
    <rect width="${project.width}" height="${project.height}" fill="#ffffff"/>
    <g>${fills.join('')}</g>
    <g fill="#111111" font-family="Arial, Helvetica, sans-serif" font-size="0.39" font-weight="700" text-anchor="middle" dominant-baseline="middle">${codes.join('')}</g>
    <g fill="none" shape-rendering="geometricPrecision">${lines.join('')}</g>
  </svg>`;
}

function buildCompactLegend(project: PatternProject, mode: PatternPrintMode) {
  const stats = collectPrintStats(project);
  if (!stats.length) return '';
  return `<section class="legend-section" aria-label="完整色卡"><div class="legend-title">色号清单 · ${stats.length}色</div><div class="legend-grid">${stats.map((item) => {
    const swatch = mode === 'color' ? item.hex : grayscaleHex(item.hex);
    const textColor = readableTextColor(swatch);
    return `<article class="legend-card" style="background:${swatch};color:${textColor}"><strong>${escapeHtml(item.code)} <span>(${item.count.toLocaleString('zh-CN')})</span></strong></article>`;
  }).join('')}</div></section>`;
}

export function buildPatternPrintHtml(project: PatternProject, mode: PatternPrintMode) {
  const error = getPatternPrintError(project);
  if (error) throw new Error(error);
  const metrics = getPatternPrintMetrics(project);
  const modeLabel = mode === 'color' ? '彩色打印' : '单色打印';
  const title = escapeHtml(project.name || '未命名图纸');
  return `<!doctype html>
<html lang="zh-CN"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${title}-A4打印版</title>
<style>
  :root{color-scheme:light;font-family:"Microsoft YaHei","PingFang SC",Arial,sans-serif;color:#171412;background:#e9e5df}*{box-sizing:border-box}html,body{margin:0;padding:0}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}.print-actions{position:sticky;top:0;z-index:10;padding:10px 16px;display:flex;align-items:center;justify-content:center;gap:12px;background:#26211d;color:#fff;box-shadow:0 4px 18px rgba(0,0,0,.2)}.print-actions strong{font-size:13px}.print-actions span{font-size:11px;color:#d9d1c9}.print-actions button{min-height:36px;padding:0 14px;border:0;border-radius:9px;background:#f4675c;color:#fff;cursor:pointer;font-weight:800}.sheet{position:relative;width:210mm;height:297mm;overflow:hidden;background:#fff}.pattern-header{position:absolute;left:5mm;right:5mm;top:2mm;height:6mm;display:grid;grid-template-columns:minmax(0,1fr) auto 52mm;align-items:center;gap:5mm}.pattern-header h1{margin:0;overflow:hidden;font-size:3.3mm;line-height:1;text-overflow:ellipsis;white-space:nowrap}.pattern-header p{margin:0;color:#5c5650;font-size:2.2mm;white-space:nowrap}.calibration{width:50mm;padding-top:.8mm;border-top:.25mm solid #111;color:#39332f;font-size:1.9mm;text-align:center}.pattern-content{position:absolute;left:0;right:0;top:9mm;bottom:4mm;display:flex;align-items:center;flex-direction:column}.pattern-board{width:100%;min-height:0;flex:1 1 auto;display:flex;align-items:center;justify-content:center}.pattern-grid{display:block;flex:0 0 auto}.legend-section{position:relative;width:195mm;flex:0 0 auto;padding:2.4mm .7mm .4mm;border-top:.28mm solid #bdb5ad;background:#faf8f5}.legend-title{position:absolute;left:1mm;top:.35mm;color:#423b35;font-size:2mm;font-weight:800;line-height:1}.legend-grid{width:100%;display:grid;grid-template-columns:repeat(9,minmax(0,1fr));gap:1mm}.legend-card{height:6mm;padding:.4mm;display:flex;align-items:center;justify-content:center;border:.2mm solid rgba(0,0,0,.24);border-radius:1mm;text-align:center}.legend-card strong{max-width:100%;overflow:hidden;font-size:2.4mm;line-height:1;text-overflow:ellipsis;white-space:nowrap}.legend-card span{font-weight:700}@page{size:A4 portrait;margin:0}@media screen{main{padding:18px}.sheet{margin:0 auto 18px;box-shadow:0 12px 42px rgba(54,43,35,.18)}}@media print{.print-actions{display:none!important}main{padding:0}.sheet{margin:0;box-shadow:none}}
</style></head><body>
<div class="print-actions"><div><strong>A4单页${modeLabel}</strong><br><span>图纸与完整色卡同页 · 请选择“实际大小/100%”</span></div><button type="button" onclick="window.print()">重新打开打印</button></div>
<main><section class="sheet pattern-page">
  <header class="pattern-header"><h1>${title}</h1><p>${project.width} × ${project.height}格 · ${metrics.colorCount}色 · ${metrics.beadCount.toLocaleString('zh-CN')}颗</p><div class="calibration">校准线50mm · 单格${PRINT_CELL_SIZE_MM}mm</div></header>
  <div class="pattern-content"><div class="pattern-board">${buildPatternSvg(project, mode)}</div>${buildCompactLegend(project, mode)}</div>
</section></main>
<script>window.addEventListener('load',function(){window.setTimeout(function(){window.print()},350)})</script></body></html>`;
}

export function openPatternPrintWindow(project: PatternProject, mode: PatternPrintMode) {
  const html = buildPatternPrintHtml(project, mode);
  const printWindow = window.open('', '_blank', 'width=1000,height=800');
  if (!printWindow) throw new Error('浏览器拦截了打印窗口，请允许本站打开新窗口后重试');
  printWindow.opener = null;
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
}
